import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
// import '@adornis/digitale-helden-shared/client/theme/buildify/d-buildify.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { MarketingPage, SEOMeta } from '@adornis/digitale-helden-shared/db/MarketingPage.js';
import { signalToObservable } from '@adornis/functional-lit/signal-to-observable.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { routingStateSignal } from '@adornis/router/client/routing-state-signal.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { distinctUntilChanged, firstValueFrom, of, switchMap, tap, timer } from 'rxjs';
import { getMarketingBuildifyExtensions } from '../../buildify/client/helpers.js';
import { MarketingVisualizeBuildify } from '../../features/buildify/client/MarketingVisualizeBuildify.js';
import './marketing-not-found.js';

@customElement('marketing-fallback')
export class MarketingFallback extends ChemistryLitElement {
  @property({ attribute: false }) extensions = new RXController(this, getMarketingBuildifyExtensions());

  @state() private _isLoading = false;
  @state() routingState = new RXController(this, signalToObservable(routingStateSignal));
  @state() private readonly _marketingPage = new RXController(
    this,
    this.routingState.observable.pipe(
      tap(() => (this._isLoading = true)),
      switchMap(routingState => {
        const path = routingState?.path;
        if (!path) return of(null);
        return MarketingPage.getPageByPath(path)(MarketingPage.allFields);
      }),
      tap(async () => {
        await firstValueFrom(timer(1000));
        this._isLoading = false;
      }),
    ),
  );

  override connectedCallback(): void {
    super.connectedCallback();

    this._marketingPage.observable.pipe(distinctUntilChanged()).subscribe(page => {
      this.parentElement?.scrollTo({ top: 0, behavior: 'smooth' });

      if (!page) {
        // meta tags wieder entfernen
        return;
      }

      const head = document.head;

      // title
      const title = document.createElement('title');
      title.innerText = page?.seoMeta?.title ?? 'Digitale Helden';

      // description
      const metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      metaDescription.setAttribute('content', page?.seoMeta?.description ?? '');

      const existingTitle = head.querySelector('title');
      if (existingTitle) head.removeChild(existingTitle);

      const existingMetaDescription = head.querySelector('meta[name="description"]');
      if (existingMetaDescription) head.removeChild(existingMetaDescription);

      // append meta tags
      head.appendChild(metaDescription);
      head.appendChild(title);
    });
  }

  override render() {
    const isLoading =
      this._marketingPage.isLoading ||
      (this._marketingPage.value && this.routingState.value?.path !== this._marketingPage.value?.path) ||
      this._isLoading;

    html` <d-text> Wird geladen... </d-text> `;

    const page = this._marketingPage.value;
    if (page && !page.seoMeta) page.seoMeta = new SEOMeta({});

    const pageContent = page
      ? MarketingVisualizeBuildify({
          entityData: page,
          value: page.content,
          mode: 'view',
          onAfterCorrectionCreated: draft => {
            goTo(`/admin/edit-draft/${draft._id}`);
          },
        })
      : html` <marketing-not-found></marketing-not-found> `;

    return html`
      <div
        ${css({
          width: '100%',
          height: '100vh',
          overflow: 'hidden',
          position: 'relative',
          display: isLoading ? 'block' : 'contents',
        })}
      >
        <!-- overlay -->
        <d-flex
          center
          crossaxis-center
          ${css({
            zIndex: '999',
            background: '#fff',
            width: '100%',
            height: '100%',
            position: 'absolute',
            display: isLoading ? 'flex' : 'none',
          })}
          space="md"
        >
          <d-loader></d-loader>
        </d-flex>
        ${pageContent}
      </div>
    `;
  }
}
